import { RefObject, useEffect } from 'react'

/**
 * Custom hook that adds a click event listener on the document that gets
 * executed when clicking outside a given DOM Node passed as a ref.
 */
export const useClickOutside = (
  ref: RefObject<HTMLElement> | null,
  callback: (event: React.MouseEvent | MouseEvent) => void,
  deps: unknown[] = [],
  skipVisibilityCheck = false
) => {
  const handleClick = (e: React.MouseEvent | MouseEvent) => {
    if (
      ref &&
      ref.current &&
      !ref.current.contains(e.target as Node) &&
      (skipVisibilityCheck || isVisible(e.target as HTMLElement))
    ) {
      callback(e)
    }
  }

  useEffect(() => {
    if (ref) {
      document.addEventListener('click', handleClick)
    }

    return () => {
      if (ref) {
        document.removeEventListener('click', handleClick)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callback, ...deps])
}

function isVisible(elem: HTMLElement) {
  if (process.env.NODE_ENV === 'test') return true

  return (
    !!elem &&
    !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length)
  )
}
